import React, { useEffect, useMemo, useState } from 'react';

import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Tab, { TabGroup } from '@amzn/meridian/tab';
import Text from '@amzn/meridian/text';

import {
    MssPivotGridData,
    useProgressiveMssData,
    useProgressiveRoyaltiesData
} from 'src/hooks/analytics/useProgressiveServiceData';
import { useSearchParam } from 'src/hooks/useSearchParam/useSearchParam';

import { CustomViewFilter, FilterState } from '../CustomViewFilter';
import { transformMssGridToPivotGrid } from '../utils/dataTransformation';
import { formatMonthForDisplay, generateMonthRange, getRecentMonths } from '../utils/dateUtils';
import { MssView } from './MssView';
import { MssViewPivot } from './MssViewPivot';
import { RoyaltiesView } from './RoyaltiesView';
import { RoyaltiesViewPivot } from './RoyaltiesViewPivot';
import { UmgMssView } from './UmgMssView';

/**
 * Props for the TabContent wrapper component
 */
interface TabContentProps {
    /** Whether this tab content should be visible */
    isActive: boolean;
    /** The content to render inside the tab */
    children: React.ReactNode;
}

/**
 * TabContent wrapper component that handles visibility of tab content
 * using CSS display property to avoid unmounting/remounting components
 */
const TabContent: React.FC<TabContentProps> = ({ isActive, children }) => (
    <div
        style={{
            display: isActive ? 'flex' : 'none',
            flexDirection: 'column'
        }}
    >
        {children}
    </div>
);

/**
 * Props for the ServiceMetrics component
 */
export interface ServiceMetricsProps {
    /** The name of the service (e.g., 'AMU', 'AMF', 'Prime') */
    serviceName: string;
    /** The display title for the service metrics page */
    title: string;
    /** A brief description of what the service metrics show */
    description: string;
    /** Service ID for API calls */
    serviceId: number;
}

/**
 * ServiceMetrics component displays analytics data for a specific service
 * with tabbed views for royalties and MSS data in both trending and raw formats.
 */
export const ServiceMetrics: React.FC<ServiceMetricsProps> = ({ serviceName, title, description, serviceId }) => {
    // Use URL parameter for tab state with default fallback
    const [currentTab, setCurrentTab] = useSearchParam('tab', 'royalties-pivot');

    // Month selection state - initialize with recent 6 months
    const recentMonths = getRecentMonths(6);
    const [selectedMonths, setSelectedMonths] = useState<string[]>(recentMonths);

    // Initialize month selector with display format
    const [startMonth, setStartMonth] = useState(formatMonthForDisplay(recentMonths[0]));
    const [endMonth, setEndMonth] = useState(formatMonthForDisplay(recentMonths[recentMonths.length - 1]));

    // Filter state management
    const [filterState, setFilterState] = useState<FilterState>({
        selectedTerritories: [],
        selectedVendors: []
    });

    // Sequential loading: Start MSS loading only after royalties completes
    const [shouldLoadMss, setShouldLoadMss] = useState(false);

    // Use hooks to fetch data with selected months
    const royaltiesState = useProgressiveRoyaltiesData({
        serviceId,
        months: selectedMonths
    });

    // Only start MSS loading after royalties is complete
    const mssState = useProgressiveMssData({
        serviceId,
        months: shouldLoadMss ? selectedMonths : []
    });

    // Start MSS loading when royalties reaches 100%
    useEffect(() => {
        if (royaltiesState.completionPercentage === 100 && !shouldLoadMss) {
            setShouldLoadMss(true);
        }
    }, [royaltiesState.completionPercentage, shouldLoadMss]);

    // Optimized loading state - show loading only until royalties initial load is complete
    // This allows users to start with Royalties Trending tab immediately
    const isLoading = !royaltiesState.isInitialLoadComplete;
    const hasError = royaltiesState.error || mssState.error;

    const isMssLoading = !mssState.isInitialLoadComplete;

    // Territory rankings: Calculate once when first month's data is available
    const [territoryRankings, setTerritoryRankings] = useState<string[]>([]);

    useEffect(() => {
        // Only calculate territory rankings once when first data is available and rankings haven't been set
        if (royaltiesState.data.length > 0 && territoryRankings.length === 0) {
            // Get first month's data for UMG territory breakdown
            const firstMonthData = royaltiesState.data.filter((item) => item.vendor === 'Universal & Affiliates');

            if (firstMonthData.length === 0) return;

            // Use UMG data for territory ranking
            const territoryEarnings = new Map<string, number>();
            firstMonthData.forEach((item) => {
                territoryEarnings.set(item.territory, item.earningInUSD);
            });

            const rankings = Array.from(territoryEarnings.entries())
                .sort(([, a], [, b]) => b - a)
                .map(([territory]) => territory);

            setTerritoryRankings(rankings);

            // Auto-select top 10 territories immediately
            if (filterState.selectedTerritories.length === 0) {
                let topTerritories = rankings.slice(0, 10);
                if (!topTerritories.includes('AU')) {
                    topTerritories = [...topTerritories, 'AU'];
                }
                setFilterState((prev) => ({
                    ...prev,
                    selectedTerritories: topTerritories
                }));
            }
        }
    }, [royaltiesState.data, territoryRankings.length, filterState.selectedTerritories.length]);

    // Get all available territories (for filter options)
    const availableTerritories = useMemo(() => {
        const territories = new Set<string>();
        royaltiesState.data.forEach((item) => territories.add(item.territory));
        mssState.data.forEach((item) => territories.add(item.territory));

        // Return territories sorted by ranking if available, otherwise alphabetical
        if (territoryRankings.length > 0) {
            const rankedTerritories = territoryRankings.filter((territory) => territories.has(territory));
            const unrankedTerritories = Array.from(territories)
                .filter((territory) => !territoryRankings.includes(territory))
                .sort();
            return [...rankedTerritories, ...unrankedTerritories];
        }

        return Array.from(territories).filter(Boolean).sort();
    }, [royaltiesState.data, mssState.data, territoryRankings]);

    // Vendor rankings: Track vendor count and re-sort when new vendors appear
    const [previousVendorCount, setPreviousVendorCount] = useState(0);
    const [sortedVendors, setSortedVendors] = useState<string[]>([]);

    const availableVendors = useMemo(() => {
        const vendors = new Set<string>();
        royaltiesState.data.forEach((item) => vendors.add(item.vendor));
        mssState.data.forEach((item) => vendors.add(item.vendor));

        const currentVendorCount = vendors.size;

        // Check if new vendors appeared
        if (currentVendorCount > previousVendorCount && royaltiesState.data.length > 0) {
            // Get the most recent month's data for vendor sorting
            const latestMonth = Math.max(...royaltiesState.data.map((item) => parseInt(item.month)));
            const latestMonthData = royaltiesState.data.filter((item) => parseInt(item.month) === latestMonth);

            // Aggregate earnings by vendor for the latest month
            const vendorEarnings = new Map<string, number>();
            latestMonthData.forEach((item) => {
                const current = vendorEarnings.get(item.vendor) || 0;
                vendorEarnings.set(item.vendor, current + item.earningInUSD);
            });

            // Sort vendors by earnings (descending)
            const newSortedVendors = Array.from(vendorEarnings.entries())
                .sort(([, a], [, b]) => b - a)
                .map(([vendor]) => vendor);

            setSortedVendors(newSortedVendors);
            setPreviousVendorCount(currentVendorCount);

            return newSortedVendors;
        }

        // Return existing sorted vendors or fallback to alphabetical
        if (sortedVendors.length > 0) {
            // Ensure all current vendors are included
            const vendorArray = Array.from(vendors);
            const sortedExisting = sortedVendors.filter((vendor) => vendors.has(vendor));
            const newVendors = vendorArray.filter((vendor) => !sortedVendors.includes(vendor)).sort();
            return [...sortedExisting, ...newVendors];
        }

        return Array.from(vendors).filter(Boolean).sort();
    }, [royaltiesState.data, mssState.data, previousVendorCount, sortedVendors]);

    // Apply filters to data
    const filteredRoyaltiesData = useMemo(() => {
        let filtered = royaltiesState.data;

        if (filterState.selectedTerritories.length > 0) {
            filtered = filtered.filter((item) => filterState.selectedTerritories.includes(item.territory));
        }

        if (filterState.selectedVendors.length > 0) {
            filtered = filtered.filter((item) => filterState.selectedVendors.includes(item.vendor));
        }

        return filtered;
    }, [royaltiesState.data, filterState]);

    const filteredMssData = useMemo(() => {
        let filtered = mssState.data;

        if (filterState.selectedTerritories.length > 0) {
            filtered = filtered.filter((item) => filterState.selectedTerritories.includes(item.territory));
        }

        if (filterState.selectedVendors.length > 0) {
            filtered = filtered.filter((item) => filterState.selectedVendors.includes(item.vendor));
        }

        return filtered;
    }, [mssState.data, filterState]);

    // Transform only for AMU service
    const filteredMssPivotData = useMemo(() => {
        return serviceId === 1 ? transformMssGridToPivotGrid(filteredMssData) : filteredMssData;
    }, [filteredMssData]);

    // Filter UMG data for UMG MSS tab
    const filteredUmgMssData = useMemo(() => {
        return serviceId === 1
            ? (filteredMssPivotData as MssPivotGridData[]).filter((item) => item.vendor === 'Universal & Affiliates')
            : [];
    }, [filteredMssPivotData]);

    // Handle month range change from MonthSelector
    const handleMonthRangeChange = (startMonthDisplay: string, endMonthDisplay: string) => {
        setStartMonth(startMonthDisplay);
        setEndMonth(endMonthDisplay);

        // Convert to API format and generate range
        const startMonthApi = startMonthDisplay.replace('-', '');
        const endMonthApi = endMonthDisplay.replace('-', '');
        const monthRange = generateMonthRange(startMonthApi, endMonthApi);

        setSelectedMonths(monthRange);
        // Reset MSS loading when months change
        setShouldLoadMss(false);
        // Reset territory rankings when months change so they recalculate with new first month
        setTerritoryRankings([]);
        // Reset vendor tracking when months change
        setPreviousVendorCount(0);
        setSortedVendors([]);
    };

    const mssLoader = (
        <Column spacing="300" alignmentHorizontal="center" alignmentVertical="center" height="100%">
            <Loader />
            <Text>Loading MSS data...</Text>
        </Column>
    );

    return (
        <Column width="100%" heights={['fit', 'fit', 'fit', 'fill']}>
            <Heading level={2}>{title}</Heading>
            <Text type="b200" color="secondary">
                {description}
            </Text>
            <Row width="100%" widths="fill">
                <CustomViewFilter
                    availableTerritories={availableTerritories}
                    availableVendors={availableVendors}
                    filterState={filterState}
                    onFilterChange={setFilterState}
                    startMonth={startMonth}
                    endMonth={endMonth}
                    onMonthRangeChange={handleMonthRangeChange}
                />
            </Row>
            <Row width="100%" widths="fill" alignmentVertical="stretch">
                <Column width="100%" heights="fill">
                    {hasError ? (
                        // Show error in tabs area
                        <Column heights="fill" alignmentHorizontal="center" alignmentVertical="center">
                            <Text color="error">Error loading data: {royaltiesState.error || mssState.error}</Text>
                        </Column>
                    ) : (
                        <Column heights={['fit', 'fill']}>
                            <TabGroup value={currentTab} onChange={setCurrentTab} fill="line">
                                <Tab value="royalties-pivot">Royalties Trending</Tab>
                                <Tab value="mss-pivot">MSS Trending</Tab>
                                <Tab value="royalties">Royalties Data</Tab>
                                <Tab value="mss">MSS Data</Tab>
                                {serviceId === 1 && <Tab value="umg-mss">UMG MSS</Tab>}
                            </TabGroup>
                            <Column heights="fill">
                                <TabContent isActive={currentTab === 'royalties'}>
                                    <RoyaltiesView
                                        data={filteredRoyaltiesData}
                                        loading={false}
                                        error={royaltiesState.error || undefined}
                                        isDataFullyLoaded={royaltiesState.completionPercentage === 100}
                                        completionPercentage={royaltiesState.completionPercentage}
                                    />
                                </TabContent>

                                <TabContent isActive={currentTab === 'royalties-pivot'}>
                                    <RoyaltiesViewPivot
                                        data={filteredRoyaltiesData}
                                        loading={!royaltiesState.isInitialLoadComplete}
                                        error={royaltiesState.error || undefined}
                                        isDataFullyLoaded={royaltiesState.completionPercentage === 100}
                                        completionPercentage={royaltiesState.completionPercentage}
                                        selectedMonths={selectedMonths}
                                    />
                                </TabContent>

                                <TabContent isActive={currentTab === 'mss'}>
                                    <MssView
                                        data={filteredMssData}
                                        loading={false}
                                        error={mssState.error || undefined}
                                        isDataFullyLoaded={mssState.completionPercentage === 100}
                                        completionPercentage={mssState.completionPercentage}
                                        serviceId={serviceId}
                                    />
                                </TabContent>

                                <TabContent isActive={currentTab === 'mss-pivot'}>
                                    <MssViewPivot
                                        data={filteredMssPivotData}
                                        loading={isMssLoading}
                                        error={mssState.error || undefined}
                                        isDataFullyLoaded={mssState.completionPercentage === 100}
                                        completionPercentage={mssState.completionPercentage}
                                        serviceId={serviceId}
                                        selectedMonths={selectedMonths}
                                    />
                                </TabContent>
                                {serviceId === 1 && (
                                    <TabContent isActive={currentTab === 'umg-mss'}>
                                        <UmgMssView
                                            data={filteredUmgMssData}
                                            loading={false}
                                            error={mssState.error || undefined}
                                            isDataFullyLoaded={mssState.completionPercentage === 100}
                                            completionPercentage={mssState.completionPercentage}
                                            selectedMonths={selectedMonths}
                                        />
                                    </TabContent>
                                )}
                            </Column>
                        </Column>
                    )}
                </Column>
            </Row>
        </Column>
    );
};
